import { toInteger } from "lodash";
import { parse, stringifyUrl } from "query-string";
import React, { useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import usePagination, { PaginationKey } from "./usePagination";

interface Props {
  paginationKey: PaginationKey;
  itemsCount: number;
}

const TablePagination: React.FC<Props> = ({ paginationKey, itemsCount }) => {
  const location = useLocation();
  const history = useHistory();
  const changeResultsPerPage = usePagination(
    (state) => state.changeResultsPerPage
  );
  const { page, pages, startIndex, endIndex, resultsPerPage } = usePagination(
    useCallback(
      (state) =>
        state.getPaginationInfo(paginationKey, itemsCount, location.search),
      [paginationKey, itemsCount, location.search]
    )
  );

  const getNewUrlForPage = useCallback(
    (newPage: number) => {
      const queryParams = parse(location.search);
      return stringifyUrl({
        url: location.pathname,
        query: {
          ...queryParams,
          [`${paginationKey}-page`]: newPage
        }
      });
    },
    [location.pathname, location.search, paginationKey]
  );

  const handlePreviousButton = useCallback(() => {
    history.push(getNewUrlForPage(page - 1));
  }, [getNewUrlForPage, history, page]);

  const handleNextButton = useCallback(() => {
    history.push(getNewUrlForPage(page + 1));
  }, [getNewUrlForPage, history, page]);

  if (itemsCount === 0) {
    return null;
  }

  return (
    <nav
      className="bg-white bg-gray-50 dark:bg-gray-800 px-4 py-3 flex items-center justify-between sm:px-6 border-t border-gray-200 dark:border-gray-500"
      aria-label="Pagination"
    >
      <div className="flex items-center">
        <select
          className="py-2 sm:text-sm rounded-md dark:bg-gray-800"
          onChange={(e) => {
            changeResultsPerPage(paginationKey, toInteger(e.target.value));
          }}
          value={resultsPerPage}
        >
          {[5, 25, 50, 100, 500].map((number) => (
            <option key={number} value={number}>
              {number}
            </option>
          ))}
        </select>

        <p className="ml-2 text-sm text-gray-700 dark:text-gray-50">
          items per page
        </p>
      </div>

      <div className="hidden sm:block">
        <p className="text-sm text-gray-700 dark:text-gray-50">
          Showing <span className="font-medium">{startIndex + 1}</span> to{" "}
          <span className="font-medium">{endIndex}</span> of{" "}
          <span className="font-medium">{itemsCount}</span> results
        </p>
      </div>

      <div className="flex justify-between sm:justify-end">
        <button
          onClick={handlePreviousButton}
          disabled={page === 1}
          className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-default dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-400 dark:disabled:hover:border-gray-700 dark:disabled:hover:bg-gray-900 dark:disabled:hover:text-gray-400"
        >
          Previous
        </button>

        <button
          onClick={handleNextButton}
          disabled={page === pages}
          className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-default dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-400 dark:disabled:hover:border-gray-700 dark:disabled:hover:bg-gray-900 dark:disabled:hover:text-gray-400"
        >
          Next
        </button>
      </div>
    </nav>
  );
};

export default TablePagination;
